import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAlertStream — opsora real-time alert WebSocket hook.
 *
 * Returns:
 *   alerts            — list of alerts the client has received so far (newest first)
 *   newAlertCount     — number of unread alerts since last `clearNew()`
 *   clearNew()        — reset the unread counter
 *   connectionStatus  — 'connecting' | 'connected' | 'disconnected' | 'unauthenticated' | 'error'
 *   lastNewAlertId    — id of the most recent alert (used for flash highlights)
 *
 * Auto-reconnects with exponential backoff (1s → 30s).
 */
export function useAlertStream(initialAlerts = []) {
  const [alerts, setAlerts] = useState(initialAlerts);
  const [newAlertCount, setNewAlertCount] = useState(0);
  const [connectionStatus, setStatus] = useState("disconnected");
  const [lastNewAlertId, setLastNewAlertId] = useState(null);

  const wsRef = useRef(null);
  const reconnectDelayRef = useRef(1000);
  const mountedRef = useRef(true);
  const reconnectTimerRef = useRef(null);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    const token = localStorage.getItem("opsora_access_token");
    if (!token) {
      setStatus("unauthenticated");
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const proto = backendUrl.startsWith("https://") ? "wss://" : "ws://";
    const host = backendUrl.replace(/^https?:\/\//, "");
    const url = `${proto}${host}/api/ws/alerts?token=${encodeURIComponent(token)}`;

    setStatus("connecting");
    let ws;
    try {
      ws = new WebSocket(url);
    } catch {
      setStatus("error");
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("connected");
      reconnectDelayRef.current = 1000;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "new_alert" && msg.alert) {
          setAlerts((prev) => {
            // Dedupe by id
            if (prev.some((a) => a.id === msg.alert.id)) return prev;
            return [msg.alert, ...prev].slice(0, 100);
          });
          setNewAlertCount((n) => n + 1);
          setLastNewAlertId(msg.alert.id);

          if (typeof document !== "undefined" && document.hidden && typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
              new Notification("Opsora Alert", { body: msg.alert.title || "New alert" });
            } catch { /* notifications optional */ }
          }
        }
        if (msg.type === "alert_acknowledged" && msg.alert_id) {
          setAlerts((prev) =>
            prev.map((a) => (a.id === msg.alert_id ? { ...a, acknowledged: true } : a))
          );
          // Decrement unread count if the acked alert is one we have already counted.
          setNewAlertCount((n) => (n > 0 ? n - 1 : 0));
        }
      } catch {
        // silently ignore malformed payloads
      }
    };

    ws.onclose = (event) => {
      wsRef.current = null;
      if (!mountedRef.current) return;
      if (event.code === 4001) {
        setStatus("unauthenticated");
        return;
      }
      setStatus("disconnected");
      reconnectTimerRef.current = setTimeout(() => {
        reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 30000);
        connect();
      }, reconnectDelayRef.current);
    };

    ws.onerror = () => {
      setStatus("error");
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(1000, "unmount"); } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearNew = useCallback(() => setNewAlertCount(0), []);

  return { alerts, newAlertCount, clearNew, connectionStatus, lastNewAlertId };
}
